import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ApiError, SESSION_ENDED_EVENT, TWO_FACTOR_REQUIRED_EVENT, type Viewer } from "./api.js";
import { unregisterNativePush } from "./native-app.js";

/**
 * Delete the offline copy of a user's workspace that earlier versions kept in
 * IndexedDB (`astrum-connect:<userId>`).
 *
 * Connect is online-only now and never opens that database, but one left on a
 * shared machine still holds someone's messages and tasks. Best-effort and
 * fire-and-forget: deleting a database that does not exist is a no-op.
 */
function purgeLegacyOfflineCache(userId: string): void {
  try {
    globalThis.indexedDB?.deleteDatabase(`astrum-connect:${userId}`);
  } catch {
    // Storage blocked (private window, policy) — there is nothing to purge.
  }
}

type AuthState = {
  viewer: Viewer | null;
  /** True until the first session check resolves, so we never flash the login screen. */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ requiresTwoFactor: boolean; challengeId?: string }>;
  verifyTwoFactor: (challengeId: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** True once a session that was open has ended on its own, for the login screen to say so. */
  sessionEnded: boolean;
  /** Re-reads the session, for flows that authenticate outside signIn —
   *  accepting an invitation, for one, which issues a session of its own. */
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionEnded, setSessionEnded] = useState(false);

  // Establish whether a session cookie is already valid before rendering
  // anything that depends on identity.
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ user: Viewer }>("/auth/session")
      .then((res) => {
        if (!cancelled) setViewer(res.user);
      })
      .catch((error: unknown) => {
        // A 401 here is the normal "not signed in" case, not a failure.
        if (!cancelled && !(error instanceof ApiError && error.isUnauthenticated)) {
          console.error("Session check failed", error);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /*
   * Adopt the browser's timezone once, for a viewer still on the untouched
   * "UTC" default.
   *
   * The timezone decides when a person's morning digest and reminders arrive
   * (7am local), and it defaults to UTC on the server. Most people never open
   * Profile to change it, so their reminders were firing at UTC — hours off
   * their real morning. Detecting it here, right after the viewer loads, fixes
   * that for everyone rather than only those who find the setting.
   *
   * Guarded so it never overrides a real choice: it runs only when the stored
   * value is exactly "UTC" AND the browser reports a different zone. Someone in
   * London or who deliberately picked UTC is left alone, since their browser
   * zone is UTC too. Best-effort and silent — a person's timezone appearing as
   * their actual one needs no announcement, and a failure just leaves the
   * default in place to be set from Profile.
   */
  useEffect(() => {
    if (!viewer) return;
    const stored = viewer.timezone ?? "UTC";
    let browserZone = "UTC";
    try {
      browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return;
    }
    if (stored !== "UTC" || browserZone === "UTC") return;

    let cancelled = false;
    api
      .put<{ timezone: string }>("/auth/profile", { timezone: browserZone })
      .then(() => {
        if (!cancelled) setViewer((prev) => (prev ? { ...prev, timezone: browserZone } : prev));
      })
      .catch(() => {
        // Left on the default; the person can still set it from Profile.
      });
    return () => {
      cancelled = true;
    };
    // Keyed on the viewer's id so it re-evaluates when a different person signs
    // in, not on every viewer object change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer?.id]);

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await api.post<{ user?: Viewer; requiresTwoFactor?: boolean; challengeId?: string }>("/auth/login", {
      email,
      password,
    });
    if (res.requiresTwoFactor && res.challengeId) {
      return { requiresTwoFactor: true, challengeId: res.challengeId };
    }
    if (res.user) {
      setSessionEnded(false);
      setViewer(res.user);
    }
    return { requiresTwoFactor: false };
  }, []);

  const verifyTwoFactor = useCallback(async (challengeId: string, code: string) => {
    const res = await api.post<{ user: Viewer }>("/auth/two-factor/verify", { challengeId, code });
    setSessionEnded(false);
    setViewer(res.user);
  }, []);

  useEffect(() => {
    if (viewer) purgeLegacyOfflineCache(viewer.id);
  }, [viewer]);

  const signOut = useCallback(async () => {
    // Clear local state even if the request fails — the user asked to leave.
    try {
      // Before the session ends: the server only removes a phone for its owner.
      await unregisterNativePush();
      await api.post("/auth/logout");
    } finally {
      setViewer(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<{ user: Viewer }>("/auth/session");
      setViewer(res.user);
    } catch (error) {
      // Same rule as the initial check: a 401 means "not signed in", which is
      // an answer rather than a failure.
      if (!(error instanceof ApiError && error.isUnauthenticated)) throw error;
      setViewer(null);
    }
  }, []);

  // A request answered TWO_FACTOR_REQUIRED: the setting was switched on while
  // this session was open. Re-reading the session flips the viewer to "setup
  // required", which RequireAuth turns into the enrolment screen.
  useEffect(() => {
    const onRequired = () => void refresh().catch(() => undefined);
    window.addEventListener(TWO_FACTOR_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(TWO_FACTOR_REQUIRED_EVENT, onRequired);
  }, [refresh]);

  // A request found the session gone. Dropping the viewer hands the screen to
  // RequireAuth, which sends the person to sign in and back here afterwards.
  // Listened for only while signed in: the cold-start check also answers
  // UNAUTHENTICATED for someone who simply has not signed in yet, and that is
  // not a session ending. No request is made, so it cannot loop.
  useEffect(() => {
    if (!viewer) return;
    const onEnded = () => {
      setSessionEnded(true);
      setViewer(null);
    };
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
  }, [viewer]);

  const value = useMemo<AuthState>(
    () => ({ viewer, loading, signIn, verifyTwoFactor, signOut, sessionEnded, refresh }),
    [viewer, loading, signIn, verifyTwoFactor, signOut, sessionEnded, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
